<template>
  <div class="w-full">
    <div class="flex flex-col">
      <div class="mb-16 flex justify-between items-end gap-8">
        <SparkSelect
          v-model="selectedPricingConfigId"
          label="Pricing Configuration"
          :options="availablePricingConfigs"
          name="pricing-config"
          class="w-1/2"
          @update:model-value="updatePricingConfig"
        />
        <div class="flex gap-8 items-start">
          <SparkButton
            v-if="isCustomized"
            variant="outlined"
            medium
            @click="handleExportClick"
            title="Export tiered pricing data"
            custom="whitespace-nowrap"
          >
            <i class="fas fa-download mr-8"></i>
            Export
          </SparkButton>
          <SparkButton
            v-if="!isCustomized"
            variant="outlined"
            medium
            @click="initializeCustomization"
            title="Switch to customization mode to edit all entries"
            custom="whitespace-nowrap"
          >
            <i class="fas fa-edit mr-8"></i>
            Customize
          </SparkButton>
          <SparkButton
            v-if="isCustomized"
            variant="outlined"
            medium
            @click="resetToDefaults"
            title="Reset to default calculated values"
            custom="whitespace-nowrap"
          >
            <i class="fas fa-undo mr-8"></i>
            Reset
          </SparkButton>
        </div>
      </div>

      <div class="mb-24">
        <div v-if="loading" class="flex justify-center py-16">
          <SparkSpinner />
        </div>

        <div v-else class="bg-white border border-gray-200 rounded-8 overflow-hidden">
          <table class="w-full table-fixed">
            <thead class="border-b-50">
              <tr>
                <th colspan="2" class="px-12 py-8 text-left text-13 text-gray-600 font-medium w-200">Lot Size</th>
                <th class="px-12 py-8 text-right text-13 text-gray-600 font-medium w-300">Cost per Lot</th>
                <th v-if="hasAnyNumericPrices" class="px-12 py-8 text-right text-13 text-gray-600 font-medium w-150">Margin</th>
                <th class="px-12 py-8 text-right text-13 text-gray-600 font-medium w-300">Price per Lot</th>
                <th class="w-100"></th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr v-for="(row, index) in unifiedTableData" :key="index" class="hover:bg-gray-25">
                <td class="px-12 py-8">
                  <div class="flex flex-row gap-4">
                    <SparkInput
                      v-if="row.isCustomLotSize"
                      v-model="customLotSize"
                      name="custom-lot-size-input"
                      type="number"
                      custom="w-60 !border-transparent hover:!border-gray-200 focus:!border-primary-600 !mb-0 !mt-0 !text-black"
                      placeholder="Enter lot size"
                      @change="calculateCustomLotSize"
                      @blur="calculateCustomLotSize"
                    />
                    <span v-else class="text-13 text-black pl-16">{{ row.lotSize }}</span>
                  </div>
                </td>
                <td>
                  <div class="ml-8">
                    <SparkBadge v-if="row.isActualLotSize" variant="success" class="truncate whitespace-nowrap px-8">
                      <span class="px-2 text-center">Actual Lot Size</span>
                    </SparkBadge>
                    <SparkBadge v-if="row.isBuildJobSize" variant="outlined" class="truncate whitespace-nowrap px-8">
                      <span class="px-2 text-center">Build Job Size</span>
                    </SparkBadge>
                    <SparkBadge v-if="row.isCustomLotSize" variant="outlined" class="truncate whitespace-nowrap px-60">
                      <span class="px-2 text-center">Custom (Not Saved)</span>
                    </SparkBadge>
                    <SparkBadge v-if="row.isSavedEntry && !row.isCustomLotSize" variant="success" class="truncate whitespace-nowrap px-8">
                      <span class="px-2 text-center">Saved Entry</span>
                    </SparkBadge>
                  </div>
                </td>

                <td class="px-12 py-8 text-right">
                  <div class="flex flex-col">
                    <span class="text-right block text-14 pt-16 whitespace-nowrap">
                      <template v-if="isValidNumber(row.costPerLot)">
                        <span class="text-black pr-16">{{ $formatTwoDecimalPlaces(row.costPerLot) }}</span>
                        <span class="text-gray-600"> €</span>
                      </template>
                      <span v-else>-</span>
                    </span>
                    <span v-if="isValidNumber(row.costPerLot)" class="text-right block text-11 mt-8 whitespace-nowrap">
                      <span class="text-gray-400"
                        >per Part {{ $formatTwoDecimalPlaces(row.costPerLot / row.lotSize) }} €</span
                      >
                    </span>
                  </div>
                </td>

                <td v-if="hasAnyNumericPrices" class="px-12 py-8 text-right">
                  <div class="flex items-center justify-end gap-8 pb-8">
                    <SparkInput
                      v-if="isCustomized && row.isSavedEntry"
                      :model-value="row.customMargin ? $formatOneDecimalPlace(row.customMargin) : ''"
                      name="margin-input"
                      type="text"
                      custom="w-100 text-right !border-transparent hover:!border-gray-200 focus:!border-primary-600 !mb-0 !mt-0 !text-black"
                      :placeholder="$formatOneDecimalPlace(row.margin)"
                      @input="handleMarginInput($event, row)"
                      @blur="handleMarginBlur(row)"
                    />
                    <SparkInput
                      v-else-if="row.isCustomLotSize"
                      :model-value="row.customMargin ? $formatTwoDecimalPlaces(row.customMargin) : ''"
                      name="custom-margin-input"
                      type="text"
                      custom="w-100 text-right !border-transparent hover:!border-gray-200 focus:!border-primary-600 !mb-0 !mt-0 !text-primary-1000 placeholder:!text-black"
                      :placeholder="$formatOneDecimalPlace(row.margin)"
                      @input="handleCustomMarginInput($event)"
                      @blur="handleCustomMarginBlur"
                    />
                    <span v-else class="text-13 text-black whitespace-nowrap" :title="!isCustomized ? 'Click Customize All to change margin' : ''">
                      <span v-if="row.isCustomText || row.margin === null">-</span>
                      <span v-else>{{ $formatOneDecimalPlace(row.margin) }}</span>
                    </span>
                    <span v-if="!row.isCustomText && row.margin !== null" class="text-13 text-gray-600">%</span>
                  </div>
                </td>

                <td class="px-12 py-8 text-right">
                  <div class="flex flex-col">
                    <div class="flex justify-end">
                      <template v-if="isCustomized && row.isSavedEntry">
                        <div class="flex flex-col">
                          <div class="flex justify-end items-center gap-8">
                            <SparkInput
                              :model-value="row.customPrice ? $formatTwoDecimalPlaces(row.customPrice) : ''"
                              name="price-input"
                              type="text"
                              custom="w-120 text-right !border-transparent hover:!border-gray-200 focus:!border-primary-600 !mb-0 !mt-0 !text-black"
                              :placeholder="$formatTwoDecimalPlaces(row.pricePerLot)"
                              @input="handlePriceInput($event, row)"
                              @blur="handlePriceBlur(row)"
                            />
                            <span class="text-13 text-gray-600">€</span>
                          </div>
                          <span v-if="isValidNumber(row.pricePerLot)" class="text-right block text-11 mt-8 whitespace-nowrap">
                            <span class="text-gray-400"
                              >per Part {{ $formatTwoDecimalPlaces(row.pricePerLot / row.lotSize) }} €</span
                            >
                          </span>
                        </div>
                      </template>
                      <template v-else-if="row.isCustomLotSize">
                        <div class="flex flex-col">
                          <div class="flex justify-end items-center gap-8">
                            <SparkInput
                              :model-value="row.customPrice ? $formatTwoDecimalPlaces(row.customPrice) : ''"
                              name="custom-price-input"
                              type="text"
                              custom="w-120 text-right !border-transparent hover:!border-gray-200 focus:!border-primary-600 !mb-0 !mt-0 !text-black"
                              :placeholder="$formatTwoDecimalPlaces(row.pricePerLot)"
                              @input="handleCustomPriceInput($event)"
                              @blur="handleCustomPriceBlur"
                            />
                            <span class="text-13 text-gray-600">€</span>
                          </div>
                          <span
                            v-if="isValidNumber(row.pricePerLot) && row.lotSize > 1"
                            class="text-right block text-11 mt-8 whitespace-nowrap"
                          >
                            <span class="text-gray-400"
                              >per Part {{ $formatTwoDecimalPlaces(row.pricePerLot / row.lotSize) }} €</span
                            >
                          </span>
                        </div>
                      </template>
                      <template v-else>
                        <div class="flex flex-col">
                          <span class="text-right block text-14 pt-16 whitespace-nowrap" :title="!isCustomized ? 'Click Customize All to change price' : ''">
                            <template v-if="isValidNumber(row.pricePerLot) && !row.isCustomText">
                              <span class="text-black pr-16">{{ $formatTwoDecimalPlaces(row.pricePerLot) }}</span>
                              <span class="text-gray-600"> €</span>
                            </template>
                            <template v-else-if="row.isCustomText">
                              <span class="text-black">{{ row.pricePerLot }}</span>
                            </template>
                            <template v-else>
                              <span class="text-black">-</span>
                            </template>
                          </span>
                          <span
                            v-if="isValidNumber(row.pricePerLot) && row.lotSize > 1 && !row.isCustomText"
                            class="text-right block text-11 mt-8 whitespace-nowrap"
                          >
                            <span class="text-gray-400"
                              >per Part {{ $formatTwoDecimalPlaces(row.pricePerLot / row.lotSize) }} €</span
                            >
                          </span>
                        </div>
                      </template>
                    </div>
                  </div>
                </td>
                <td>
                  <div class="flex justify-end mx-4 pb-16 gap-4" title="Row actions" style="min-width: 80px">
                    <SparkButton
                      variant="outlined"
                      custom="p-4 border-none"
                      v-show="isCustomized && row.isSavedEntry"
                      small
                      @click="revertToOriginalPricing(row)"
                      title="Revert to initial price"
                    >
                      <i class="fas fa-undo"></i>
                    </SparkButton>
                    <SparkButton
                      v-if="(row.isCustomLotSize || (row.isSavedEntry && isCustomized)) && !row.isActualLotSize"
                      variant="outlined"
                      custom="p-4 border-none text-red-600 hover:text-red-700"
                      small
                      @click="deletePricingEntry(row)"
                      title="Delete this pricing entry"
                    >
                      <i class="fas fa-trash"></i>
                    </SparkButton>
                  </div>
                </td>
              </tr>
              <tr v-if="!customLotSizeData">
                <td :colspan="allPricesAreText ? 4 : 5">
                  <div class="py-4 ml-4">
                    <SparkButton custom="p-4 border-none" variant="primary" medium @click="addCustomLotSize">
                      <i class="fas fa-plus mr-8"></i>
                      Add
                    </SparkButton>
                  </div>
                </td>
              </tr>
              <tr v-if="customLotSizeData">
                <td :colspan="allPricesAreText ? 4 : 5">
                  <div class="py-4 ml-4 flex gap-8">
                    <SparkButton custom="p-4 border-none" variant="primary" medium @click="saveCustomLotSizeAsEntry">
                      <i class="fas fa-save mr-8"></i>
                      Save
                    </SparkButton>
                    <SparkButton custom="p-4 border-none" variant="secondary" medium @click="clearCustomLotSize">
                      <i class="fas fa-times mr-8"></i>
                      Cancel
                    </SparkButton>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="!activeProcessChain.custom_price_text" class="mt-24 mb-40">
        <h3 class="font-semibold text-15 mb-16">Price Analysis</h3>
        <PriceChart :x-values="chartData.x_values" :y-values="chartData.y_values" :loading="isLoadingPrices" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

import PriceChart from './PriceChart.vue';

import SparkSelect from '@/components/SparkComponents/SparkSelect.vue';
import SparkSpinner from '@/components/SparkComponents/SparkSpinner.vue';
import SparkButton from '@/components/SparkComponents/SparkButton.vue';
import SparkBadge from '@/components/SparkComponents/SparkBadge.vue';
import SparkInput from '@/components/SparkComponents/SparkInput.vue';

export default {
  name: 'PriceCard',

  components: {
    PriceChart,
    SparkSelect,
    SparkSpinner,
    SparkButton,
    SparkBadge,
    SparkInput,
  },

  data() {
    return {
      selectedPricingConfigId: null,
      loading: false,
      isLoadingPrices: false,
      priceAnalysis: {
        x_values: [],
        y_values: [],
      },
      priceAnalysisData: null,
      lotSizes: [1, 5, 10],
      customLotSize: null,
      customLotSizeData: null,
      isCustomized: false,
    };
  },

  computed: {
    ...mapState(['investigationDetails', 'part', 'pricingOptions']),

    activeProcessChain() {
      return this.part.process_chains[this.investigationDetails.uid];
    },

    availablePricingConfigs() {
      return this.pricingOptions.map(config => ({
        value: config.uid,
        label: config.name,
      }));
    },

    hasCustomPricing() {
      return this.unifiedTableData.some(row => row.hasCustomMargin || row.hasCustomPrice);
    },

    allPricesAreText() {
      return this.unifiedTableData.length > 0 && this.unifiedTableData.every(row => row.isCustomText);
    },

    hasAnyNumericPrices() {
      return this.unifiedTableData.length > 0 && this.unifiedTableData.some(row => !row.isCustomText);
    },

    unifiedTableData() {
      if (!this.priceAnalysis?.results) return [];

      const regularRows = this.priceAnalysis.results.map(result => {
        const lotSize = result.lot_size;
        const costPerPart = result.cost_pp;
        const pricePerPart = result.price_pp;
        
        const costPerLot = result.cost_per_lot || (costPerPart * lotSize);
        const pricePerLot = result.price_per_lot || (pricePerPart * lotSize);

        const actualLotSize = this.activeProcessChain?.lot_size || 1;
        const partsPerJob = this.activeProcessChain?.parts_pj || 1;

        const isActualLotSize = lotSize === actualLotSize;
        const isBuildJobSize = lotSize === partsPerJob;

        if (result.is_custom_text) {
          return {
            lotSize,
            costPerLot,
            margin: result.margin,
            pricePerLot: result.price_per_lot,
            isActualLotSize,
            isBuildJobSize,
            isCustomText: true,
            customMargin: null,
            customPrice: null,
            hasCustomMargin: false,
            hasCustomPrice: false,
            originalMargin: null,
            originalPricePerLot: null,
            isCustomLotSize: false,
            isSavedEntry: false,
          };
        }

        if (result.is_saved_entry) {
          const margin = result.margin || 0;
          return {
            lotSize,
            costPerLot,
            margin: margin,
            pricePerLot: pricePerLot,
            isActualLotSize,
            isBuildJobSize,
            isCustomText: result.is_custom_text,
            customMargin: margin,
            customPrice: pricePerLot,
            hasCustomMargin: true,
            hasCustomPrice: true,
            originalMargin: margin,
            originalPricePerLot: pricePerLot,
            isCustomLotSize: false,
            isSavedEntry: true,
          };
        }

        const originalPricePerLot = pricePerLot;
        const originalMargin =
          costPerLot > 0 ? parseFloat((((originalPricePerLot - costPerLot) / costPerLot) * 100).toFixed(2)) : 0;

        const overwrites = this.activeProcessChain?.overwrites || {};
        const customPriceKey = `custom_price_${lotSize}`;
        const customMarginKey = `custom_margin_${lotSize}`;

        const hasCustomPrice = overwrites[customPriceKey] && overwrites[customPriceKey].user !== undefined;
        const hasCustomMargin = overwrites[customMarginKey] && overwrites[customMarginKey].user !== undefined;

        let currentPricePerLot = originalPricePerLot;
        let currentMargin = originalMargin;
        let customPrice = null;
        let customMargin = null;

        if (hasCustomPrice && hasCustomMargin) {
          currentPricePerLot = parseFloat(overwrites[customPriceKey].user.toFixed(2));
          customPrice = currentPricePerLot;
          currentMargin =
            costPerLot > 0 ? parseFloat((((currentPricePerLot - costPerLot) / costPerLot) * 100).toFixed(2)) : 0;
          customMargin = currentMargin;
        } else if (hasCustomPrice) {
          currentPricePerLot = parseFloat(overwrites[customPriceKey].user.toFixed(2));
          customPrice = currentPricePerLot;
          currentMargin =
            costPerLot > 0 ? parseFloat((((currentPricePerLot - costPerLot) / costPerLot) * 100).toFixed(2)) : 0;
        } else if (hasCustomMargin) {
          currentMargin = parseFloat(overwrites[customMarginKey].user.toFixed(2));
          customMargin = currentMargin;
          currentPricePerLot = parseFloat((costPerLot * (1 + currentMargin / 100)).toFixed(2));
        }

        return {
          lotSize,
          costPerLot,
          margin: currentMargin,
          pricePerLot: currentPricePerLot,
          isActualLotSize,
          isBuildJobSize,
          isCustomText: false,
          customMargin,
          customPrice,
          hasCustomMargin,
          hasCustomPrice,
          originalMargin,
          originalPricePerLot,
          isCustomLotSize: false,
          isSavedEntry: false,
        };
      });

      if (this.customLotSizeData) {
        const customRow = {
          lotSize: this.customLotSize || '',
          costPerLot: this.customLotSizeData.costPerLot,
          margin: this.customLotSizeData.margin,
          pricePerLot: this.customLotSizeData.pricePerLot,
          isActualLotSize: false,
          isBuildJobSize: false,
          isCustomText: false,
          customMargin: this.customLotSizeData.margin,
          customPrice: this.customLotSizeData.pricePerLot,
          hasCustomMargin: true,
          hasCustomPrice: true,
          originalMargin: this.customLotSizeData.margin,
          originalPricePerLot: this.customLotSizeData.pricePerLot,
          isCustomLotSize: true,
          isSavedEntry: false,
        };
        regularRows.push(customRow);
      }

      return regularRows;
    },

    chartData() {
      if (!this.priceAnalysis?.results) return { x_values: [], y_values: [] };

      const numericalResults = this.priceAnalysis.results.filter(result => !result.is_custom_text);

      return {
        x_values: numericalResults.map(result => result.lot_size),
        y_values: numericalResults.map(result => result.price_pp),
      };
    },
  },

  watch: {
    selectedPricingConfigId: {
      immediate: true,
      handler(newConfigId) {
        if (newConfigId) {
          this.calculatePrices();
        }
      },
    },
  },

  async mounted() {
    this.initializePricingConfig();
  },

  methods: {
    async handleExportClick() {
      try {
        await this.$store.state.application.axiosInstance.post(
          `/api/v1/tiered-pricing-export/${this.activeProcessChain.process_chain_id}/`,
          {}
        );

        await new Promise(resolve => setTimeout(resolve, 1000));

        const response = await this.$store.state.application.axiosInstance.get(
          `/api/v1/tiered-pricing-export/${this.activeProcessChain.process_chain_id}/`,
          {
            responseType: 'blob',
          }
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `tiered_pricing_${this.activeProcessChain.process_chain_id}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        this.$root.notifySuccess('Export Successful', 'Tiered pricing data has been exported successfully');
      } catch (error) {
        this.$root.notifyError('Export Error', error);
      }
    },

    isValidNumber(value) {
      return value !== null && value !== undefined && !isNaN(value);
    },

    handleMarginInput(event, row) {
      const value = event.target.value;
      const parsed = parseFloat(value);
      if (!isNaN(parsed)) {
        row.customMargin = parsed;
      }
    },

    handleMarginBlur(row) {
      if (row.customMargin !== null && row.customMargin !== undefined) {
        this.updateMargin(row);
      }
    },

    handlePriceInput(event, row) {
      const value = event.target.value;
      const parsed = parseFloat(value);
      if (!isNaN(parsed)) {
        row.customPrice = parsed;
      }
    },

    handlePriceBlur(row) {
      if (row.customPrice !== null && row.customPrice !== undefined) {
        this.updatePrice(row);
      }
    },

    handleCustomMarginInput(event) {
      const value = event.target.value;
      const parsed = parseFloat(value);
      if (!isNaN(parsed)) {
        const customRow = this.unifiedTableData.find(row => row.isCustomLotSize);
        if (customRow) {
          customRow.customMargin = parsed;
        }
      }
    },

    handleCustomMarginBlur() {
      this.updateCustomLotSizeMargin();
    },

    handleCustomPriceInput(event) {
      const value = event.target.value;
      const parsed = parseFloat(value);
      if (!isNaN(parsed)) {
        const customRow = this.unifiedTableData.find(row => row.isCustomLotSize);
        if (customRow) {
          customRow.customPrice = parsed;
        }
      }
    },

    handleCustomPriceBlur() {
      this.updateCustomLotSizePrice();
    },

    initializePricingConfig() {
      this.selectedPricingConfigId =
        this.activeProcessChain.pricing_config_id || this.availablePricingConfigs[0]?.value;
    },

    async calculatePrices() {
      if (!this.selectedPricingConfigId || !this.activeProcessChain?.process_chain_id) return;

      try {
        this.loading = true;
        this.isLoadingPrices = true;
        const response = await this.$store.state.application.axiosInstance.post(
          `/api/v1/pricing-config-analysis/${this.selectedPricingConfigId}/`,
          {
            process_chain_id: this.activeProcessChain.process_chain_id,
          }
        );

        this.priceAnalysis = response.data;
        this.isCustomized = response.data.is_customized || false;
      } catch (error) {
        this.$root.notifyError('Price Analysis', error);
        this.priceAnalysis = { x_values: [], y_values: [], cost_pp_values: [] };
      } finally {
        this.loading = false;
        this.isLoadingPrices = false;
      }
    },

    async updatePricingConfig(configId) {
      try {
        await this.$store.state.application.axiosInstance.put(
          `/api/v1/process-chain/${this.activeProcessChain.process_chain_id}/`,
          { pricing_config_id: configId }
        );

        await this.calculatePrices();
      } catch (error) {
        this.$root.notifyError('Update Pricing Config', error);
      }
    },

    async updateMargin(row) {
      if (row.isCustomText || row.customMargin === null || row.customMargin === undefined) return;

      try {
        const marginValue = parseFloat(row.customMargin);
        if (isNaN(marginValue)) return;

        const costPerLot = row.costPerLot;
        const newPricePerLot = parseFloat((costPerLot * (1 + marginValue / 100)).toFixed(2));

        if (this.isCustomized && row.isSavedEntry) {
          await this.$store.state.application.axiosInstance.put(
            `/api/v1/tiered-pricing-entry/${this.activeProcessChain.process_chain_id}/${row.lotSize}/`,
            {
              margin: parseFloat(marginValue.toFixed(2)),
              price_per_lot: newPricePerLot,
            }
          );
          
          await this.calculatePrices();
        }
      } catch (error) {
        this.$root.notifyError('Update Margin', error);
      }
    },

    async updatePrice(row) {
      if (row.isCustomText || row.customPrice === null || row.customPrice === undefined) return;

      try {
        const priceValue = parseFloat(row.customPrice);
        if (isNaN(priceValue)) return;

        const costPerLot = row.costPerLot;
        const newMargin = costPerLot > 0 ? parseFloat((((priceValue - costPerLot) / costPerLot) * 100).toFixed(2)) : 0;

        if (this.isCustomized && row.isSavedEntry) {
          await this.$store.state.application.axiosInstance.put(
            `/api/v1/tiered-pricing-entry/${this.activeProcessChain.process_chain_id}/${row.lotSize}/`,
            {
              price_per_lot: parseFloat(priceValue.toFixed(2)),
              margin: newMargin,
            }
          );
          
          await this.calculatePrices();
        }
      } catch (error) {
        this.$root.notifyError('Update Price', error);
      }
    },



    async revertToOriginalPricing(row = null) {
      try {
        if (row) {
          if (this.isCustomized && row.isSavedEntry) {
            try {
              await this.$store.state.application.axiosInstance.delete(
                `/api/v1/tiered-pricing-customization/${this.activeProcessChain.process_chain_id}/`
              );
              
              await this.$store.state.application.axiosInstance.post(
                `/api/v1/tiered-pricing-customization/${this.activeProcessChain.process_chain_id}/`
              );
              
              await this.calculatePrices();
            } catch (error) {
              this.$root.notifyError('Revert Error', 'Failed to revert entry');
            }
          }
          
          row.customMargin = null;
          row.customPrice = null;
        } else {
          const lotSizes = this.unifiedTableData.map(row => row.lotSize);
          const resetData = {};

          lotSizes.forEach(lotSize => {
            resetData[`custom_price_${lotSize}`] = null;
            resetData[`custom_margin_${lotSize}`] = null;
          });

          await this._updatePricingFeedback(null, resetData);
        }
      } catch (error) {
        this.$root.notifyError('Revert to Original Pricing', error);
      }
    },

    calculateCustomLotSize() {
      if (!this.customLotSize) {
        this.customLotSizeData = null;
        return;
      }

      const lotSize = parseFloat(this.customLotSize);
      if (isNaN(lotSize) || lotSize <= 0) {
        this.customLotSizeData = null;
        return;
      }

      const analysisResults = this.priceAnalysis?.results || [];
      let closestResult = null;
      let minDifference = Infinity;

      for (const result of analysisResults) {
        const difference = Math.abs(result.lot_size - lotSize);
        if (difference < minDifference) {
          minDifference = difference;
          closestResult = result;
        }
      }

      if (!closestResult) {
        this.customLotSizeData = null;
        return;
      }

      const costPerPart = closestResult.cost_per_part || closestResult.cost_pp;
      const pricePerPart = closestResult.price_per_part || closestResult.price_pp;
      const costPerLot = costPerPart * lotSize;
      const pricePerLot = pricePerPart * lotSize;
      const margin = costPerLot > 0 ? parseFloat((((pricePerLot - costPerLot) / costPerLot) * 100).toFixed(2)) : 0;

      this.customLotSizeData = {
        lotSize,
        costPerLot,
        pricePerLot,
        margin,
        costPerPart,
      };
    },

    clearCustomLotSize() {
      this.customLotSize = null;
      this.customLotSizeData = null;
    },

    addCustomLotSize() {
      this.customLotSize = '';
      const actualLotSizeEntry = this.unifiedTableData.find(row => row.isActualLotSize);
      const costPerPart = actualLotSizeEntry ? actualLotSizeEntry.costPerLot / actualLotSizeEntry.lotSize : 0;
      
      this.customLotSizeData = {
        lotSize: '',
        costPerLot: 0,
        pricePerLot: 0,
        margin: 0,
        costPerPart: costPerPart,
      };
    },

    updateCustomLotSizeMargin() {
      if (!this.customLotSizeData || !this.customLotSize) return;

      const lotSize = parseFloat(this.customLotSize);
      if (isNaN(lotSize) || lotSize <= 0) return;

      const customRow = this.unifiedTableData.find(row => row.isCustomLotSize);
      if (!customRow) return;

      const margin = parseFloat(customRow.customMargin);
      if (isNaN(margin)) return;

      const costPerPart = this.customLotSizeData.costPerPart;
      const costPerLot = costPerPart * lotSize;
      const pricePerLot = costPerLot * (1 + margin / 100);

      this.customLotSizeData.margin = parseFloat(margin.toFixed(2));
      this.customLotSizeData.costPerLot = parseFloat(costPerLot.toFixed(2));
      this.customLotSizeData.pricePerLot = parseFloat(pricePerLot.toFixed(2));
    },

    updateCustomLotSizePrice() {
      if (!this.customLotSizeData || !this.customLotSize) return;

      const lotSize = parseFloat(this.customLotSize);
      if (isNaN(lotSize) || lotSize <= 0) return;

      const customRow = this.unifiedTableData.find(row => row.isCustomLotSize);
      if (!customRow) return;

      const pricePerLot = parseFloat(customRow.customPrice);
      if (isNaN(pricePerLot)) return;

      const costPerPart = this.customLotSizeData.costPerPart;
      const costPerLot = costPerPart * lotSize;
      const margin = costPerLot > 0 ? ((pricePerLot - costPerLot) / costPerLot) * 100 : 0;

      this.customLotSizeData.pricePerLot = parseFloat(pricePerLot.toFixed(2));
      this.customLotSizeData.margin = parseFloat(margin.toFixed(2));
      this.customLotSizeData.costPerLot = parseFloat(costPerLot.toFixed(2));
    },

    async _updatePricingFeedback(lotSize, updateData) {
      const response = await this.$store.state.application.axiosInstance.put(
        `/api/v1/process-chain-feedback-overwrite/${this.activeProcessChain.process_chain_id}/`,
        updateData
      );

      this.$store.commit('updateSingleProcessChain', response.data);
    },

    async deletePricingEntry(row) {
      if (!row.isSavedEntry) {
        this.clearCustomLotSize();
        return;
      }

      if (row.isActualLotSize) {
        this.$root.notifyError('Delete Error', 'Cannot delete the Actual Lot Size entry');
        return;
      }

      try {
        await this.$store.state.application.axiosInstance.delete(
          `/api/v1/tiered-pricing-entry/${this.activeProcessChain.process_chain_id}/${row.lotSize}/`
        );
        
        await this.calculatePrices();
        this.$root.notifySuccess('Entry Deleted', 'Pricing entry has been deleted successfully');
      } catch (error) {
        this.$root.notifyError('Delete Error', error);
      }
    },

    async revertAllPricing() {
      try {
        const lotSizes = this.unifiedTableData
          .filter(row => row.hasCustomMargin || row.hasCustomPrice)
          .map(row => row.lotSize);
        
        const resetData = {};
        lotSizes.forEach(lotSize => {
          resetData[`custom_price_${lotSize}`] = null;
          resetData[`custom_margin_${lotSize}`] = null;
        });

        await this._updatePricingFeedback(null, resetData);
        this.clearCustomLotSize();
        this.$root.notifySuccess('Reverted', 'All custom pricing has been reverted');
      } catch (error) {
        this.$root.notifyError('Revert Error', error);
      }
    },

    async saveCustomLotSizeAsEntry() {
      if (!this.customLotSizeData) return;

      try {
        await this.$store.state.application.axiosInstance.post(
          `/api/v1/tiered-pricing-entry/${this.activeProcessChain.process_chain_id}/`,
          {
            lot_size: this.customLotSize,
            cost_per_lot: this.customLotSizeData.costPerLot,
            margin: this.customLotSizeData.margin,
            price_per_lot: this.customLotSizeData.pricePerLot,
          }
        );

        this.clearCustomLotSize();
        await this.calculatePrices();
        this.$root.notifySuccess('Entry Saved', 'Custom lot size has been saved as a pricing entry');
      } catch (error) {
        this.$root.notifyError('Save Error', error);
      }
    },

    async initializeCustomization() {
      try {
        await this.$store.state.application.axiosInstance.post(
          `/api/v1/tiered-pricing-customization/${this.activeProcessChain.process_chain_id}/`
        );

        await this.calculatePrices();
        this.$root.notifySuccess('Customization Mode', 'You can now edit and delete all pricing entries');
      } catch (error) {
        this.$root.notifyError('Customization Error', error);
      }
    },

    async resetToDefaults() {
      try {
        await this.$store.state.application.axiosInstance.delete(
          `/api/v1/tiered-pricing-customization/${this.activeProcessChain.process_chain_id}/`
        );

        await this.calculatePrices();
        this.$root.notifySuccess('Reset Complete', 'Tiered pricing has been reset to default calculated values');
      } catch (error) {
        this.$root.notifyError('Reset Error', error);
      }
    },


  },
};
</script>
